import React, { useState } from 'react';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import { get, invoke, isNil } from 'lodash';

const checkBoxStyle = css`
  display: flex;
  position: relative;
  width: fit-content;
  cursor: pointer;
  color: #2a2928;
  user-select: none;
  align-items: center;
  margin: 0;

  &:focus-within svg {
    outline-style: auto;
  }

  & > svg {
    margin-right: 0.25rem;
  }

  & > input {
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
    outline: none;
  }
`;

const SquareCheck = ({ checked }) => {
  if (checked) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
        <rect width="24" height="24" rx="6" fill="#7B1A27" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.9841 6.93913C18.2387 7.15127 18.2731 7.52961 18.0609 7.78417L10.5609 16.7842C10.453 16.9137 10.2956 16.9918 10.1272 16.9994C9.95879 17.0071 9.79494 16.9435 9.67574 16.8243L5.67574 12.8244C5.44142 12.5901 5.44142 12.2102 5.67573 11.9758C5.91005 11.7415 6.28995 11.7415 6.52426 11.9758L10.0597 15.5112L17.1391 7.01595C17.3512 6.76139 17.7295 6.72699 17.9841 6.93913Z"
          fill="white"
        />
      </svg>
    );
  } else {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
        <rect x="0.5" y="0.5" width="23" height="23" rx="5.5" fill="white" stroke="#7D7B78" />
      </svg>
    );
  }
};

SquareCheck.propTypes = {
  checked: PropTypes.bool.isRequired,
};

const CircleCheck = ({ checked }) => {
  if (checked) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
        <circle cx="12" cy="12" r="11.5" fill="white" stroke="#7D7B78" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4Z"
          fill="#7B1A27"
        />
      </svg>
    );
  } else {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
        <g clipPath="url(#clip0_1903_20874)">
          <circle cx="12" cy="12" r="11.5" fill="white" stroke="#7D7B78" />
        </g>
        <defs>
          <clipPath id="clip0_1903_20874">
            <rect width="24" height="24" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }
};

CircleCheck.propTypes = {
  checked: PropTypes.bool.isRequired,
};

/**
 * If "checked" prop is passed it becomes a controlled component, if not passed it holds its own checked state
 * @param props
 * @constructor
 */
const Checkbox = (props) => {
  const { checked, checkedByDefault, children, checkType = 'square' } = props;
  const [checkedState, setCheckedState] = useState(!!checkedByDefault);
  const className = get(props, 'className');

  const handleOnChange = () => {
    const newChecked = isNil(checked) ? !checkedState : !checked;
    invoke(props, 'onChange', newChecked);
    setCheckedState(newChecked);
  };

  return (
    <div className={className}>
      <label className={checkBoxStyle}>
        {checkType === 'circle' ? (
          <CircleCheck checked={isNil(checked) ? checkedState : checked} />
        ) : (
          <SquareCheck checked={isNil(checked) ? checkedState : checked} />
        )}
        {children}
        <input
          type={checkType === 'circle' ? 'radio' : 'checkbox'}
          checked={isNil(checked) ? checkedState : checked}
          aria-label={children}
          onChange={handleOnChange}
        />
      </label>
    </div>
  );
};

Checkbox.propTypes = {
  checked: PropTypes.bool,
  checkedByDefault: PropTypes.bool,
  checkType: PropTypes.oneOf(['circle', 'square']),
  onChange: PropTypes.func,
  children: PropTypes.any,
  className: PropTypes.string,
};

export default Checkbox;
